import { useEffect, useState } from 'react';
import { useStore } from '@/store/useStore';
import { fetchInvite, type InvitePublic, type InviteBranch, type InvitePosition } from '@/auth/authClient';
import { UserPlus, Loader2, Clock } from 'lucide-react';

/**
 * Регистрация по email-инвайту: ссылка вида ?invite=<token>. Сотрудник задаёт
 * логин/пароль, телефон и свой филиал. Роль/должность берутся из инвайта.
 * При регистрации создаётся карточка сотрудника (видна в «Сотрудниках»).
 */
export function RegisterScreen({ token }: { token: string }) {
  const register = useStore((s) => s.register);
  const authError = useStore((s) => s.authError);

  const [invite, setInvite] = useState<InvitePublic | null>(null);
  const [branches, setBranches] = useState<InviteBranch[]>([]);
  const [positions, setPositions] = useState<InvitePosition[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [fullName, setFullName] = useState('');
  const [loginValue, setLoginValue] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [phone, setPhone] = useState('');
  const [branchId, setBranchId] = useState('');
  const [workerType, setWorkerType] = useState('');
  const [busy, setBusy] = useState(false);
  // Регистрация по ОБЩЕЙ ссылке ушла на подтверждение — показываем спокойный экран ожидания.
  const [pending, setPending] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchInvite(token)
      .then(({ invite, branches, positions }) => { if (alive) { setInvite(invite); setBranches(branches); setPositions(positions); setFullName(invite.full_name ?? ''); if (invite.worker_type) setWorkerType(invite.worker_type); } })
      .catch((e) => { if (alive) setLoadError(e instanceof Error ? e.message : String(e)); });
    return () => { alive = false; };
  }, [token]);

  const pwMismatch = confirm.length > 0 && password !== confirm;
  const needBranch = branches.length > 0;
  const needPosition = positions.length > 0;
  // Защита от дурака: ФИО, телефон, филиал и должность — обязательны (иначе карточка пустая).
  const nameWords = fullName.trim().split(/\s+/).filter(Boolean);
  const phoneNorm = phone.replace(/[\s\-()]/g, '');
  const missing: string[] = [];
  if (nameWords.length < 2 || nameWords.some((w) => w.length < 2)) missing.push('имя и фамилию (два слова)');
  if (!/^[^\s@]+@[^\s@]+$/.test(loginValue.trim())) missing.push('логин в виде почты (имя@домен)');
  if (password.length < 6) missing.push('пароль (от 6 символов)');
  if (password !== confirm) missing.push('повтор пароля');
  if (!/^((\+7|7|8)\d{10}|\+99[468]\d{9})$/.test(phoneNorm)) missing.push('телефон (8/7/+7 или +996/+998/+994)');
  if (needBranch && !branchId) missing.push('филиал');
  if (needPosition && !workerType) missing.push('должность');
  const valid = missing.length === 0;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    try {
      const isPending = await register(token, loginValue.trim(), password, fullName.trim() || undefined, phone.trim() || undefined, branchId || undefined, workerType || undefined);
      if (isPending) setPending(true);
    } catch {
      /* authError */
    } finally {
      setBusy(false);
    }
  };

  // Заявка отправлена (общая ссылка) — спокойный экран ожидания, без авто-входа.
  if (pending) {
    return (
      <div className="h-full overflow-y-auto flex justify-center bg-surface-0 text-ink-1 p-4">
        <div className="w-full max-w-sm my-auto bg-surface-1 border border-line rounded-2xl p-6 shadow-xl text-center space-y-3">
          <div className="mx-auto w-12 h-12 rounded-full bg-accent/10 grid place-items-center">
            <Clock className="w-6 h-6 text-accent" />
          </div>
          <h1 className="text-lg font-semibold">Заявка отправлена</h1>
          <p className="text-sm text-ink-2">
            Доступ откроется после подтверждения администратором — вам сообщат.
            Пока входить не нужно.
          </p>
        </div>
      </div>
    );
  }

  return (
    // h-full + overflow-y-auto, а центрирование по вертикали — через my-auto на форме:
    // если форма выше экрана (невысокий телефон), она НЕ обрезается, а прокручивается,
    // иначе кнопка «Зарегистрироваться» уезжала за нижний край и достать её было нечем.
    <div className="h-full overflow-y-auto flex justify-center bg-surface-0 text-ink-1 p-4">
      <form onSubmit={submit} className="w-full max-w-sm my-auto bg-surface-1 border border-line rounded-2xl p-6 shadow-xl">
        <span className="text-2xl font-black bg-gradient-to-br from-accent to-warm-coral bg-clip-text text-transparent">+ERP</span>
        <h1 className="text-lg font-semibold mt-1 mb-1">Регистрация</h1>

        {loadError ? (
          <p className="text-sm text-status-refused mt-4">{loadError}</p>
        ) : !invite ? (
          <p className="text-sm text-ink-3 mt-4 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Загрузка приглашения…</p>
        ) : (
          <>
            {invite.email && <p className="text-xs text-ink-3 mb-4">Приглашение для <span className="text-ink-2">{invite.email}</span></p>}
            <div className="space-y-3">
              <div>
                <label className="field-label">ФИО*</label>
                <input className="input" value={fullName} onChange={(e) => setFullName(e.target.value)} disabled={busy} />
              </div>
              <div>
                <label className="field-label">Логин — почта*</label>
                <input className="input" autoComplete="username" value={loginValue} onChange={(e) => setLoginValue(e.target.value)} disabled={busy} placeholder="name@mail.com" />
              </div>
              <div>
                <label className="field-label">Пароль* (мин. 6)</label>
                <input className="input" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={busy} />
              </div>
              <div>
                <label className="field-label">Повтор пароля*</label>
                <input className="input" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} disabled={busy} />
                {pwMismatch && <p className="text-[11px] text-status-refused mt-1">Пароли не совпадают</p>}
              </div>
              <div>
                <label className="field-label">Телефон*</label>
                <input className="input" type="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} disabled={busy} placeholder="+7…" />
              </div>
              {needBranch && (
                <div>
                  <label className="field-label">Филиал (город)*</label>
                  <select className="input" value={branchId} onChange={(e) => setBranchId(e.target.value)} disabled={busy}>
                    <option value="">— выберите —</option>
                    {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
              )}
              {needPosition && (
                <div>
                  <label className="field-label">Должность*</label>
                  <select className="input" value={workerType} onChange={(e) => setWorkerType(e.target.value)} disabled={busy}>
                    <option value="">— выберите —</option>
                    {positions.map((p) => <option key={p.id} value={p.id}>{(p.icon ? p.icon + ' ' : '') + p.name}</option>)}
                  </select>
                </div>
              )}
            </div>

            {authError && <p className="mt-3 text-sm text-status-refused">{authError}</p>}
            {!valid && !authError && (
              <p className="mt-3 text-[12px] text-ink-3">Заполните: {missing.join(', ')}.</p>
            )}

            <button type="submit" className="btn-primary w-full mt-5 justify-center" disabled={!valid || busy}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
              Зарегистрироваться
            </button>
          </>
        )}
      </form>
    </div>
  );
}
